import { Button, DefinitionItem, DefinitionList, View } from "components/Shared";
import { translate } from "lib";
import _ from "lodash";
import React, { memo } from "react";
import { useHistory } from "react-router-dom";

/**
 * What Yardi says about the lease ITSELF, with an Edit button for the two a
 * human may correct (LeaseAgreementEdit).
 *
 * `TCode` and `Property` are shown but NOT editable: together they are the dedup
 * key the next /lease_data payload matches on, so changing either would orphan
 * this lease's whole amendment tree. `V2::LeaseAgreementsController#safe_params`
 * is the authority.
 *
 * ---------------------------------------------------------------------------
 * FOUR FIELDS, AND THAT IS THE WHOLE PAGE (Sim, 24 Aug)
 * ---------------------------------------------------------------------------
 * TCode, Property, Category (Lease Type), Lease Status. Nothing else — Sim named
 * these four and asked for the rest gone.
 *
 * The commercial fields left because they belong to the AMENDMENT (each is a
 * statement about a term; one copy per lease meant the newest payload won), and
 * they are shown on the Details tab of the amendment's own page (the Amendments
 * tab's View button) and corrected from its Edit button, the same two steps this
 * tab and LeaseAgreementEdit are. The
 * Lessee Name, the folded Term in Force and the unit count came out as well:
 * the tenant has its own tab, and the term and the units are what the Amendments,
 * Tenancies and Tree tabs are for. `actual_opening_at` is gone from the record
 * entirely — nothing read it off the agreement.
 */

interface Props {
	agreement: tcpinpoint.LeaseAgreement;
	name?: string;
	icon?: string;
	/**
	 * Drop the Edit button and show the list alone. For the read-only reuses of
	 * this tab — the finalize wizard's Lease step, which states the record and
	 * offers no way to change it. NOT a permission check: the button is hidden
	 * because the surrounding screen is read-only, not because this user may not
	 * edit. `.summary-step.read-only` would kill the click anyway, leaving a
	 * button that looks live and does nothing — worse than no button.
	 */
	hideEdit?: boolean;
}

/**
 * EVERY ROW IS ALWAYS SHOWN — a blank one reads "Unknown" rather than vanishing
 * (Sim, 27 Aug 2026). A missing Lease Status was silently absent from the list,
 * which reads as "this lease has no such field" instead of "Yardi has not sent
 * one yet"; on a four-row page it also left no way to tell a short page from a
 * broken one.
 *
 * It has to be a NODE, not a string: `DefinitionItem` renders nothing when
 * `value` is falsy — and, separately, treats the literal string `"unknown"` as
 * absent — so the muted span is what keeps the row on screen. Same `<span
 * className="muted">` the Property row below already used for its blank case, and
 * the same wording as the project workspace header's own datapoints.
 */
const unknown = <span className="muted">{translate("unknown")}</span>;

const stated = (value?: string | number | null) =>
	_.isNil(value) || value === "" ? unknown : value;

const DetailsTab = ({ agreement, hideEdit }: Props) => {
	const history = useHistory();

	return (
	// NO `title` (Sim, 26 Aug) — see AmendmentDetailsTab. The tab label says
	// Details and the page header says which lease; an h2 saying "Lease Details"
	// between them said neither.
	<View>
		{/* The amendment whose term is in force has been called off. Worth saying
		    out loud even on a page this short: the lease is running to a term
		    nobody honours, and the project that term created is closed rather than
		    deleted. The dates themselves are on the Amendments tab. */}
		{!!agreement.cancelled_amendment && (
			<div className="alert alert-warning" role="alert">
				{translate("lease_agreement_cancelled_term_warning")}
			</div>
		)}
		<DefinitionList>
			<DefinitionItem title="tcode" value={stated(agreement.tcode)} />
			<DefinitionItem title="property">
				{!!agreement.property_name ? (
					<span>
						{agreement.property_name}
						{!!agreement.property_reference_code && (
							<span className="muted"> ({agreement.property_reference_code})</span>
						)}
					</span>
				) : (
					unknown
				)}
			</DefinitionItem>
			<DefinitionItem title="lease_type" value={stated(agreement.lease_type)} />
			<DefinitionItem title="lease_status" value={stated(agreement.lease_status)} />
			{/* No Project row (Sim, 24 Aug). A lease has as many projects as it has
			    amendments that create one, so naming a single "the project" here was
			    misleading — the Amendments tab lists them all, each row linking to its
			    own. */}
		</DefinitionList>

		{/* The shared <Button>, plain and pencil-less, BELOW the list — identical to
		    the Edit on app.staging's project Lease tab, which is what this replaced
		    (Sim, 24 Aug). The pencil `Link` styling belongs to LIST ROWS (the
		    Tenancies/Tenants/amendment rows); a detail page uses the button. */}
		{!hideEdit && (
			<Button onClick={() => history.push(`/lease-agreements/${agreement.id}/edit`)}>
				{translate("edit")}
			</Button>
		)}
	</View>
	);
};

export default memo(DetailsTab);
